// BL-177 Step 2 Part B — the personal "When I press Enter" composer preference.
//
// Saved per user on their own user_profiles row (settings.communications.enter_key) through the
// bl177_set_my_enter_key RPC, so it follows the person across computer and phone.
//   'send'    (default) Enter sends; Shift+Enter adds a line break.
//   'newline' Enter adds a line break; Shift+Enter sends.
// Shift+Enter ALWAYS does the opposite of Enter. Ctrl/Cmd+Enter ALWAYS sends: in both modes, and in email too.
// The setting is one switch ("Enter sends the message") in the person's own Settings > Communications > Typing & sending;
// nothing about it shows in the messaging screens.

import React from 'react';

export const ENTER_KEY_SEND = 'send';
export const ENTER_KEY_NEWLINE = 'newline';
export const DEFAULT_ENTER_KEY = ENTER_KEY_SEND;
export const ENTER_KEY_OPTIONS = [
    [ENTER_KEY_NEWLINE, 'Line break'],
    [ENTER_KEY_SEND, 'Send message'],
];
export const ENTER_KEY_HELP = 'Ctrl/Cmd+Enter always sends. In email, Enter always adds a new line, whichever way this is set.';

// Chat-style composers only follow the preference. Email composers keep Enter = line break regardless of it
// (Ctrl/Cmd+Enter still sends there: it is a deliberate send, never a stray line break).
const CHAT_CHANNELS = ['sms', 'mms', 'whatsapp'];

export function normalizeEnterKey(value) {
    return value === ENTER_KEY_NEWLINE ? ENTER_KEY_NEWLINE : DEFAULT_ENTER_KEY;
}

export function enterKeyAppliesToChannel(channel) {
    return CHAT_CHANNELS.includes(String(channel || '').toLowerCase());
}

/**
 * What a keydown in a composer should do: 'send', or 'default' (let the browser do its normal thing,
 * which in a textarea is inserting a line break).
 * A key pressed during IME/predictive-text composition is never a send (isComposing, or the legacy
 * keyCode 229 some mobile keyboards report).
 */
export function resolveEnterKeyAction({ key, shiftKey = false, ctrlKey = false, metaKey = false, altKey = false, isComposing = false, keyCode = 13 } = {}, { mode = DEFAULT_ENTER_KEY, channel = 'sms' } = {}) {
    if (key !== 'Enter') return 'default';
    if (isComposing || keyCode === 229) return 'default';
    if (altKey) return 'default';
    if (ctrlKey || metaKey) return 'send';
    if (!enterKeyAppliesToChannel(channel)) return 'default';
    const enterSends = normalizeEnterKey(mode) === ENTER_KEY_SEND;
    // Shift+Enter is always the opposite of Enter
    return (shiftKey ? !enterSends : enterSends) ? 'send' : 'default';
}

export async function loadEnterKeyPreference(client, userId) {
    if (!client || !userId) return DEFAULT_ENTER_KEY;
    const { data, error } = await client.from('user_profiles').select('settings').eq('id', userId).maybeSingle();
    if (error) throw error;
    return normalizeEnterKey(data?.settings?.communications?.enter_key);
}

export async function saveEnterKeyPreference(client, mode) {
    const next = normalizeEnterKey(mode);
    const { error } = await client.rpc('bl177_set_my_enter_key', { p_mode: next });
    if (error) throw error;
    return next;
}

// Read by every chat composer; Communications provides the signed-in user's saved choice.
export const EnterKeyPreferenceContext = React.createContext(DEFAULT_ENTER_KEY);
